import type { DayProblem, TimeEntry } from '../utils/dayLegs';
import { formatClockTime } from '../utils/visitProjectTime';

interface GapFillChooserProps {
  gap: DayProblem;
  /** The synced store visit the gap runs into, when there is one (gapDriveCandidate). */
  driveCandidate: TimeEntry | null;
  onStoreVisit: () => void;
  onDrive: (candidate: TimeEntry) => void;
  onClose: () => void;
}

/**
 * "What filled this time?" — the one add verb for a hole in the day.
 *
 * A hole has exactly two representable answers: a store visit (which includes
 * admin and other project time — every kind of work is a visit against some
 * project), or the drive that reached the visit the gap runs into. A drive has
 * no server home of its own, so "driving" is only offered when there is a
 * visit to carry it; a bare stretch of paid idle stays visible as Unassigned
 * rather than getting a label nothing downstream can read.
 */
export default function GapFillChooser({ gap, driveCandidate, onStoreVisit, onDrive, onClose }: GapFillChooserProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="gap-fill-chooser">
        <h3 className="text-lg font-semibold text-slate-900">
          What filled {formatClockTime(gap.from)} – {formatClockTime(gap.to)}?
        </h3>
        <div className="mt-4 space-y-2">
          <button
            onClick={onStoreVisit}
            className="w-full rounded-xl border border-slate-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50"
          >
            <div className="font-medium text-slate-800">A store visit</div>
            <div className="text-xs text-slate-500">
              Record what you did — admin, training and other project time counts too
            </div>
          </button>
          {driveCandidate && (
            <button
              onClick={() => onDrive(driveCandidate)}
              className="w-full rounded-xl border border-slate-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50"
            >
              <div className="font-medium text-slate-800">
                Driving to {driveCandidate.storeName || 'the next store'}
              </div>
              <div className="text-xs text-slate-500">
                Attach this time to that visit as its drive
              </div>
            </button>
          )}
        </div>
        <button onClick={onClose} className="mt-4 w-full py-2 text-sm text-slate-500">
          Cancel
        </button>
      </div>
    </div>
  );
}
